import { useEffect, useRef, useState } from 'react';
import { useContactOptions, useMatterOptions } from '@/hooks';
import matterService from '@/services/matter/matterService';
import { KeenIcon } from '@/components';
import DataTable from 'datatables.net-react';
import DT from 'datatables.net-dt';
import 'datatables.net-select-dt';
import 'datatables.net-responsive-dt';
import { createRoot } from 'react-dom/client';
import { useAuthContext } from '@/auth';
import { Link, useNavigate } from 'react-router-dom';
import { createDataTableOptions, createDataTableConfig, ObjectToFormData, FiltersToFormData, getAccessToken, decodeHtmlEntities } from '@/utils'
import { SelectInput, DateInput, STATUS_INVOICE_LISTING_OPTIONS, FilterContainer } from '@/components/TableFilter'
import { cn } from '@/lib/utils';
import invoiceService from '@/services/billing/invoice/invoiceService';
import { goTo403 } from "@/utils/Navigation";
import { Exporter } from '@/components/exporter';
import { ColumnToggleDropdown } from '@/components/TableFilter/ColumnToggleDropdown';
import { InvoiceBillingSummary } from '@/components/invoice';

DataTable.use(DT);
const InvoiceTab = ({ dataFromParent, onNavigateFromChild, onClearChildProps }) => {
    console.log('dataFromParent', dataFromParent)
    const { logout } = useAuthContext();
    const loadContactOptions = useContactOptions({ defaultPageSize: 20 });
    const loadMatterOptions = useMatterOptions({ defaultPageSize: 20 });
    const [showFilter, setShowFilter] = useState(false);
    const [filterData, setFilterData] = useState({
        approved_by: null,
        invoice_status: null,
        payment_status: null,
        contact_id: dataFromParent?.contactId,
        matter_id: dataFromParent?.matterId,
        from_date: null,
        to_date: null,
        date_range: null,
        // invoices: dataFromParent?.invoice_ids
        settlement: dataFromParent?.settlement
    });
    const API_URL = import.meta.env.VITE_APP_BASE_URL;
    const token = getAccessToken();
    const tableRef = useRef(null);
    const filterRef = useRef(filterData);
    const Navigate = useNavigate()
    const [invoicePaymentHistory, setInvoicePaymentHistory] = useState(0.00)
    const [paymentHistoryLoading, setPaymentHistoryLoading] = useState(false)

    useEffect(() => {
        filterRef.current = filterData;
    }, [filterData]);

    const badgeClasses = {
        billed: 'badge-success',
        approved: 'badge-success',
        paid: 'badge-success',
        pending: 'badge-warning',
    }

    const paymentStatusClass = {
        pending: 'badge-warning',
        paid: 'badge-success',
        partially_paid: 'badge-info',
    }

    const columns = [
        // {
        //     title: 'S#',
        //     name: 'serial',
        //     data: null,
        //  orderable: true,se,
        //     render: (data, type, row, meta) => meta.row + 1,
        // },
        {
            data: 'number',
            title: 'Number',
            name: 'number',
            orderable: true,
            render: function (data, type, row) {
                return `<div id="number-cell-${row.id}"></div>`;
            }
        },
        {
            data: 'is_import',
            title: 'Imported Invoice',
            name: 'is_import',
            render: function (value) {
                console.log(value)
                const is_import = value === 1 ? 'Imported' : '-';
                if (value === 0) return is_import;
                return `<span class="capitalize badge badge-pill badge-outline badge-info gap-1 items-center">
                    <span class="badge badge-dot size-1.5 badge-info"></span>
                    ${is_import}
                </span>`;
            }
        },
        {
            data: 'contact.fullName',
            title: 'Contact',
            name: 'fullName',
            orderable: false,
            render: function (data, type, row) {
                return `<div id="contact-cell-${row.id}"></div>`;  // Use string template for HTML
            }
        },
        {
            data: 'matter.name',
            title: 'Matter',
            name: 'matter',
            orderable: false,
            render: function (data, type, row) {
                return `<div id="matter-cell-${row.id}"></div>`;
            }
        },
        {
            data: 'invoice_status',
            title: 'Invoice Status',
            name: 'invoice_status',
            render: function (value) {
                if (!value) return '-';
                return `<span class="capitalize badge badge-pill badge-outline ${badgeClasses[value]} gap-1 items-center">
                    <span class="badge badge-dot size-1.5 ${badgeClasses[value]}"></span>
                    ${value}
                </span>`;
            }
        },
        {
            data: 'payment_status',
            title: 'Payment Status',
            name: 'payment_status',
            render: function (value) {
                if (!value) return '-';
                return `<span class="capitalize badge badge-pill badge-outline ${paymentStatusClass[value]} gap-1 items-center">
                    <span class="badge badge-dot size-1.5 ${paymentStatusClass[value]}"></span>  
                    ${value.split('_').join(' ')}
                </span>`;
            }
        },
        {
            data: 'total',
            title: 'Total',
            name: 'total',
            render: function (data, type, row) {
                return `<div>$${row.total}</div>`;
            }
        },
        {
            data: 'settlements[0].amount',
            title: 'Settlement Amount',
            name: 'settlements',
            orderable: false,
            render: function (data, type, row) {
                const amount =
                    Array.isArray(row.settlements) && row.settlements.length > 0
                        ? row.settlements[0].amount
                        : null;
                return `<div>${amount ? `$${amount}` : '-'}</div>`;
            },
        },
        {
            data: 'created_at',
            title: 'Created At',
            name: 'created_at',
            orderable: true,
        },
        {
            data: 'invoice_age',
            title: 'Aging',
            name: 'invoice_age',
            orderable: false,
            render: function (data, type, row) {
                if (row.invoice_age === null) return '-';
                return `<div>${row.invoice_age}</div>`;
            }
        },
    ];

    const defaultVisibleColumns = [
        // 'serial',
        'number',
        // 'is_import',
        'date',
        'invoice_status',
        'payment_status',
        'approved_by',
        'total',
        'settlements',
        'fullName',
        'matter',
        'invoice_age',
        'created_at',
        'actions',
    ];

    const ajaxConfig = createDataTableConfig('/setup/invoices/data_table_list', filterRef, token, logout);

    const options = createDataTableOptions({
        order: [[8, "desc"]],
        initComplete: function () {
            const api = this.api();
            api.columns().every(function () {
                const col = this;
                const name = col.settings()[0].aoColumns[col.index()].name;
                if (name && !defaultVisibleColumns.includes(name)) {
                    col.visible(false);
                }
            });
        },
        drawCallback: function () {
            const api = this.api();
            const rows = api.rows({ page: 'current' }).data();

            rows.each((row) => {
                const contactContainer = document.getElementById(`contact-cell-${row.id}`);
                if (contactContainer) {
                    if (!contactContainer._reactRoot) {
                        contactContainer._reactRoot = createRoot(contactContainer);
                    }
                    contactContainer._reactRoot.render(renderContactName(row));
                }
                const matterContainer = document.getElementById(`matter-cell-${row.id}`);
                if (matterContainer) {
                    if (!matterContainer._reactRoot) {
                        matterContainer._reactRoot = createRoot(matterContainer);
                    }
                    matterContainer._reactRoot.render(renderMatterName(row));
                }
                const numberContainer = document.getElementById(`number-cell-${row.id}`);
                if (numberContainer) {
                    if (!numberContainer._reactRoot) {
                        numberContainer._reactRoot = createRoot(numberContainer);
                    }
                    numberContainer._reactRoot.render(renderNumber(row));
                }
            });
        },
    });

    const resetFilters = () => {
        const cleared = {
            approved_by: null,
            invoice_status: null,
            contact_id: null,
            matter_id: null,
            from_date: null,
            to_date: null,
            date_range: null,
            // invoices: null
            settlement: null
        };

        setFilterData(cleared);
        filterRef.current = cleared;
        refresh()
        fetchInvoicePaymentHistory()
        onClearChildProps?.();
    };

    const fetchInvoicePaymentHistory = async () => {
        setPaymentHistoryLoading(true)
        try {
            const formData = ObjectToFormData(filterRef.current);
            const { data, status } = await invoiceService.getRawDataReportInvoicePaymentSummary(formData);
            if (status) {
                console.log('data', data)
                setInvoicePaymentHistory(data)
            }
        } catch (error) {
            console.error('Error', error)
            toast.error('Failed to fetch Invoice Payment History.', {
                description: 'An error occurred while fetching invoice payment history. Please try again later.',

            });
        } finally {
            setPaymentHistoryLoading(false)
        }
    }

    useEffect(() => {
        fetchInvoicePaymentHistory()
    }, [])

    const handleFilterChange = (key, val) => {
        setFilterData(prev => {
            const updated = { ...prev, [key]: val };
            if (key === 'contact_id') updated.matter_id = null;
            filterRef.current = updated;
            return updated;
        });
    };

    const handleToggleFilter = () => {
        setShowFilter(prev => !prev)
    }

    const fetchLookup = async (type, ctx = {}) => {
        switch (type) {
            case 'matter': {
                const contactId = ctx?.contactId || null;
                if (contactId) {
                    const res = await invoiceService.getContactMatterRelatedData('contact', contactId);
                    const rows = res?.data?.matters || [];
                    return rows.map(m => ({ label: m.name, value: m.id }));
                }
                const matters = await matterService.getAllMatters();
                return (matters?.data || []).map(m => ({ label: m.name, value: m.id }));
            }
            default:
                return [];
        }
    };

    const refresh = () => {
        const table = tableRef.current.dt();
        table.ajax.reload();
    };

    const renderNumber = (row) => {
        const fullNumber = `INVOICE-${row.number}`;
        const { id } = row;
        // Imported invoices open like any other (OL-146).
        return (
            <span onClick={() => { Navigate(`/billing/invoice/preview/${id}`) }} className='cursor-pointer link'>{fullNumber}</span>
        )
    }

    const renderContactName = (row) => {
        if (!row.contact) {
            return <span>-</span>;
        }
        const fullName = row.contact.first_name + ' ' + row.contact.last_name;
        const { id } = row.contact;
        return (
            <span onClick={() => { Navigate(`/contacts/${id}/`) }} className='cursor-pointer link'>{fullName}</span>
        )
    }

    const renderMatterName = (row) => {
        const { matter } = row;
        const fullName = decodeHtmlEntities(matter?.name || '');

        return (
            <span
                onClick={() => Navigate(`/matters/detail/${matter?.id || ''}/`)}
                className="cursor-pointer link"
            >
                {fullName}
            </span>
        );
    }


    return (
        <>
            <InvoiceBillingSummary
                paymentData={invoicePaymentHistory}
                loading={paymentHistoryLoading}
                showTotalBlock={true}
            />
            {/* Filters */}
            <FilterContainer
                show={showFilter}
                onApply={() => {
                    refresh();
                    fetchInvoicePaymentHistory();
                }}
                onReset={resetFilters}
            >
                {/* <SelectInput
                    label="Approved By"
                    type="users"
                    value={filterData.approved_by}
                    onChange={(val) => handleFilterChange('approved_by', val)}
                    fetchLookup={fetchLookup}
                    customClass="sm:w-[32%] w-full"
                /> */}

                <SelectInput
                    label="Contact"
                    type="contact"
                    value={filterData.contact_id}
                    onChange={(val) => handleFilterChange('contact_id', val)}
                    loadOptions={loadContactOptions}
                    pageSize={20}
                    customClass="sm:w-[32%] w-full"
                />

                <SelectInput
                    label="Matter"
                    type="matter"
                    value={filterData.matter_id}
                    onChange={(val) => handleFilterChange('matter_id', val)}
                    loadOptions={loadMatterOptions}
                    pageSize={20}
                    lookupParams={{ contactId: filterData.contact_id?.value ?? null }}
                    customClass="sm:w-[32%] w-full"
                />

                <SelectInput
                    label="Invoice Status"
                    value={filterData.invoice_status}
                    onChange={(val) => handleFilterChange('invoice_status', val)}
                    staticOptions={STATUS_INVOICE_LISTING_OPTIONS.slice(0, -1)}
                    customClass="sm:w-[32%] w-full"
                />

                <SelectInput
                    label="Payment Status"
                    value={filterData.payment_status}
                    onChange={(val) => handleFilterChange('payment_status', val)}
                    staticOptions={STATUS_INVOICE_LISTING_OPTIONS.slice(-3)}
                    customClass="sm:w-[32%] w-full"
                />

                <DateInput
                    customClass="sm:w-[32%] w-full"
                    label="Created At - (From, To)"
                    mode="range"
                    value={filterData.date_range}
                    onChange={(range) => {
                        setFilterData(prev => ({
                            ...prev,
                            from_date: range?.from ?? null,
                            to_date: range?.to ?? null,
                            date_range: range,
                        }));
                    }}
                />
            </FilterContainer>

            <div className="min-w-full mb-4 card">
                <div className="card-header">
                    <div className='flex items-center gap-1'>
                        <ColumnToggleDropdown tableRef={tableRef} columns={columns} defaultVisibleColumns={defaultVisibleColumns} />
                        <h3 className="card-title">
                            {dataFromParent?.contactName
                                ? `${dataFromParent.contactName} Invoices`
                                : dataFromParent?.matterName
                                    ? `${dataFromParent.matterName} Invoices`
                                    : "All Invoices"}
                        </h3>
                    </div>

                    <div className='flex flex-wrap items-center gap-2'>
                        <button className='text-xs btn btn-sm btn-primary btn-outline' onClick={handleToggleFilter}>
                            <KeenIcon icon="filter" className="w-fit btn-icon" />
                            {showFilter ? 'Hide Advanced Filters' : 'Advanced Filters'}
                        </button>
                        <div>
                            <Exporter
                                tableRef={tableRef}
                                module="invoice"
                                // columns={[]}
                                columns={columns}
                                filters={filterData}
                            />
                        </div>
                    </div>
                </div>

                <DataTable
                    id="invoice-table"
                    ajax={ajaxConfig}
                    columns={columns}
                    options={options}
                    ref={tableRef}
                />

            </div>

        </>
    )
}

export { InvoiceTab }
